import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { DECORATIONS } from '../gen/interiors/catalog';
import { MODELS } from '../gen/interiors/models';

function shadedBasic(): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({ vertexColors: true });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nvarying vec3 vWorldN;').replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      #if defined( USE_BATCHING )
        vWorldN = normalize(mat3(modelMatrix) * mat3(batchingMatrix) * normal);
      #elif defined( USE_INSTANCING )
        vWorldN = normalize(mat3(modelMatrix) * mat3(instanceMatrix) * normal);
      #else
        vWorldN = normalize(mat3(modelMatrix) * normal);
      #endif`,
    );
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWorldN;')
      .replace('#include <color_fragment>', '#include <color_fragment>\ndiffuseColor.rgb *= 0.74 + 0.26 * vWorldN.y + 0.05 * vWorldN.x;');
  };
  m.customProgramCacheKey = () => 'furnitureShaded';
  return m;
}

/**
 * Without the downward faces at the model's base (y = 0): a floor-standing item's are never seen, but they lie in the plane of the floor slab and, above a lobby, exactly in
 * its ceiling, where they z-fought through it.
 */
function dropBaseFaces(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const pos = g.getAttribute('position');
  const keep: number[] = [];
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  for (let i = 0; i < pos.count; i += 3) {
    a.fromBufferAttribute(pos, i);
    b.fromBufferAttribute(pos, i + 1);
    c.fromBufferAttribute(pos, i + 2);
    const n = b.clone().sub(a).cross(c.clone().sub(a));
    const base = Math.max(Math.abs(a.y), Math.abs(b.y), Math.abs(c.y)) < 1e-4 && n.y < 0;
    if (!base) keep.push(i, i + 1, i + 2);
  }
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(g.attributes)) {
    const src = g.getAttribute(name);
    const arr = new Float32Array(keep.length * src.itemSize);
    keep.forEach((v, k) => {
      for (let j = 0; j < src.itemSize; j++) arr[k * src.itemSize + j] = src.array[v * src.itemSize + j];
    });
    out.setAttribute(name, new THREE.BufferAttribute(arr, src.itemSize));
  }
  g.dispose();
  return out;
}

/**
 * Furniture geometry per catalogue item: the item's model parts (boxes and cylinders in the unit box,
 * models.ts) merged into one vertex-coloured geometry, built on first use and shared by every floor.
 * Parts without their own colour take the item's main colour.
 */
export class FurnitureAssets {
  /**
   * Unlit: each instance's colour carries the baked light of its room (interiorManager), and a
   * world-normal term (tops brighter than sides, undersides darkest) gives the shared models shape.
   */
  readonly material = shadedBasic();
  private readonly cache = new Map<number, THREE.BufferGeometry>();

  geometry(kind: number): THREE.BufferGeometry {
    let g = this.cache.get(kind);
    if (g) return g;
    const deco = DECORATIONS[kind] ?? DECORATIONS[0];
    const parts = MODELS[deco.model] ?? MODELS.crate;
    const geos: THREE.BufferGeometry[] = [];
    for (const p of parts) {
      const sx = Math.max(1e-3, p.max[0] - p.min[0]);
      const sy = Math.max(1e-3, p.max[1] - p.min[1]);
      const sz = Math.max(1e-3, p.max[2] - p.min[2]);
      let pg: THREE.BufferGeometry;
      if (p.shape === 'box') pg = new THREE.BoxGeometry(sx, sy, sz);
      else {
        pg = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
        pg.scale(sx, sy, sz);
      }
      pg.translate((p.min[0] + p.max[0]) / 2, (p.min[1] + p.max[1]) / 2, (p.min[2] + p.max[2]) / 2);
      pg = pg.index ? pg.toNonIndexed() : pg;
      pg.deleteAttribute('uv');
      const c = p.color ?? deco.color;
      const n = pg.getAttribute('position').count;
      const cols = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) cols.set(c, i * 3);
      pg.setAttribute('color', new THREE.BufferAttribute(cols, 3));
      geos.push(pg);
    }
    g = mergeGeometries(geos)!;
    // Only for items standing on the floor: a ceiling light's or a picture's underside is what you see.
    if (deco.lift === 0 && !deco.tags.includes('ceiling')) g = dropBaseFaces(g);
    for (const x of geos) x.dispose();
    this.cache.set(kind, g);
    return g;
  }
}
